/*
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */

import axios from "axios";
import { defineStore, acceptHMRUpdate } from "pinia";
import {
  alfrescoApi,
  authenticationApi,
  peopleApi,
  searchApi,
  groupsApi,
} from "../AlfrescoApi";
import { loginError } from "@/services/ErrorService.js";
import { useConfigStore } from "./config";
import { useTaskNotificationStore } from "./taskNotification";
import { webscriptApi } from "../AlfrescoApi";

// TODO: move to ConfigStore
const ALF_TOKEN_TTL_MS = 60 * 1000;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * `useUserStore` is a Pinia store for managing user authentication, session, and related user data.
 *
 * The store includes state variables for managing user session details, actions for logging in and out,
 * validating sessions, and interacting with authentication systems like Keycloak and Alfresco,
 * as well as getters for computed properties like `displayName`.
 *
 * State:
 * - `token` {string} - Stores the authentication token.
 * - `isAdmin` {boolean} - Indicates whether the current user is an administrator.
 * - `isLoggedIn` {boolean} - Tracks whether the user is currently logged in.
 * - `person` {object|null} - Stores the person details of the logged-in user.
 * - `keycloak` {object|null} - Represents Keycloak authentication configuration.
 * - `homeFolder` {string|null} - Stores the user's home folder path.
 *
 * Getters:
 * - `displayName` {string} - Returns the display name of the logged-in user, or an empty string if no user is logged in.
 *
 * Actions:
 * - `logout` - Logs out the current user and stops the auto-renew for the OIDC connection.
 * - `validateSession` - Checks if the client is logged in and retrieves the Alfresco ticket using the Keycloak Bearer Token.
 * - `connectKeycloak` - Handles the redirection and connection to Keycloak for unauthenticated users.
 * - `keycloakReady` - Stores the Keycloak object and retrieves the Alfresco ticket using the Keycloak Bearer Token.
 * - `getAlfTicket` - Logs into Alfresco with the Bearer token and retrieves the Alfresco ticket.
 * - `getPerson` - Retrieves the person details for the currently authenticated user.
 * - `login` - Attempts to log in a user with a username and password.
 * - `apiLogin` - Handles Basic Authentication with Alfresco.
 * - `publiclogoff` - Logs off a public account if currently authenticated.
 * - `publiclogin` - Logs in with a public account from a specified public workspace.
 */
export const useUserStore = defineStore({
  id: "UserStore",
  state: () => ({
    token: "",
    isAdmin: false,
    isLoggedIn: false,
    person: null,
    keycloak: null,
    homeFolder: null,
    isPristyAdmin: null,
  }),
  getters: {
    displayName(state) {
      return state.person == null ? "" : state.person.displayName;
    },
  },
  actions: {
    /**
     * Logout current user and stop auto-renew for oidc connection
     * @return {Promise<axios.AxiosResponse<any>>}
     */
    async logout() {
      useTaskNotificationStore().stopPolling();

      if (alfrescoApi) {
        await alfrescoApi.logout();
      }

      this.$patch({
        token: "",
        isAdmin: false,
        person: null,
        apiAlfresco: null,
        isLoggedIn: false,
        isPristyAdmin: null,
      });

      const config = useConfigStore();
      if (
        config.AUTH === "keycloak" ||
        config.AUTH === "lemonldap" ||
        config.AUTH === "azuread"
      ) {
        clearTimeout(this.keycloakRenewTO);

        try {
          await axios.get(`${config.ALFRESCO_HOST}/alfresco/logout`);
        } catch (error) {
          console.error("Error during Alfresco logout:", error);
        }

        localStorage.removeItem("ticket-ECM");
        localStorage.removeItem("ACS_USERNAME");
        this.keycloak.logout();
      }
    },
    /**
     * Check if the client is logged and get alf_ticket using Keycloak Bearer Token
     * @return {Promise<{id:string}>} Current alf_token
     */
    validateSession() {
      if (!alfrescoApi.isLoggedIn()) {
        this.isLoggedIn = false;
        return Promise.resolve(undefined);
      }

      const config = useConfigStore();
      if (
        config.AUTH === "keycloak" ||
        config.AUTH === "lemonldap" ||
        config.AUTH === "azuread"
      ) {
        return this.getAlfTicket();
      }

      return authenticationApi
        .validateTicket()
        .then(() => {
          this.isLoggedIn = true;
        })
        .catch(() => {
          this.isLoggedIn = false;
        });
    },
    /**
     * Redirect to Keycloak if unauthent, else retrieve current Person
     * form Alfresco.
     * @return {Promise<void>}
     */
    connectKeycloak: async function () {
      // We wait for Keycloak init, then we can call all methods safely
      while (this.keycloak == null || this.keycloak.createLoginUrl === null) {
        await sleep(100);
      }
      if (this.keycloak.authenticated) {
        while (!this.isLoggedIn) {
          await sleep(100);
          await this.validateSession();
        }
        if (this.person === null) {
          await this.getPerson();
        }
      } else {
        const config = useConfigStore();
        if (config.AUTH === "azuread") {
          // AAD does not support "code id_token token"
          this.keycloak.responseType = config.OIDC_RESPONSE_TYPE;
        }
        const loginUrl = this.keycloak.createLoginUrl();
        window.location.replace(loginUrl);
      }
    },

    /**
     * Store Keycloak object and get alf_ticket using Keycloak Bearer Token.
     * We will ask alf_ticket every ALF_TOKEN_TTL_MS (60s)
     * @param {any} _keycloak Keycloak object yet authenticate
     * @return {Promise<{id: string}>}
     */
    keycloakReady(_keycloak) {
      this.keycloak = _keycloak;
      return this.getAlfTicket().then((alfToken) => {
        this.keycloakRenewTO = setInterval(this.getAlfTicket, ALF_TOKEN_TTL_MS);
        return alfToken;
      });
    },
    /**
     * Log to Alfresco with Bearer token and set alf_token
     * @return {Promise<{id:string} | undefined>} Current alf_token or undefined if not using OIDC
     */
    getAlfTicket() {
      const config = useConfigStore();
      if (
        config.AUTH !== "keycloak" &&
        config.AUTH !== "lemonldap" &&
        config.AUTH !== "azuread"
      ) {
        console.warn(
          "getAlfTicket() won't work in standard AUTH configuration",
        );
        return Promise.resolve(undefined);
      }
      return axios
        .get(
          `${config.ALFRESCO_HOST}/alfresco/api/-default-/public/authentication/versions/1/tickets/-me-`,
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `Bearer ${this.keycloak.token}`,
            },
          },
        )
        .then((response) => response.data.entry)
        .then((alftoken) => {
          alfrescoApi.setTicket(alftoken.id, null);
          window.localStorage.setItem(
            "ACS_USERNAME",
            this.keycloak.idTokenParsed.preferred_username,
          );
          this.isLoggedIn = true;
          return alftoken;
        })
        .catch((error) => {
          this.isLoggedIn = false;
          throw loginError(error);
        });
    },
    /**
     * Get Person for user '-me-'. Help to check if we are always authenticate
     * @return {Promise<Person>} Person object of current user.
     */
    getPerson() {
      return peopleApi
        .getPerson("-me-")
        .then((data) => data.entry)
        .then((p) => {
          this.isLoggedIn = true;
          this.person = p;
          this.isAdmin = p.capabilities?.isAdmin === true;
          return p;
        })
        .then((p) => {
          return searchApi
            .search({
              query: {
                language: "afts",
                query: `cm:userName:${p.id}`,
              },
              filterQueries: [{ query: "TYPE:'cm:person'" }],
              include: ["properties"],
            })
            .then((results) => {
              if (results.list.pagination.count > 1) {
                console.warn("More than one Person for " + p);
              } else if (results.list.pagination.count < 0) {
                console.warn("More than one Person for " + p);
                return {};
              }
              return results.list.entries[0].entry;
            })
            .then((person) => {
              this.homeFolder = person.properties["cm:homeFolder"];
            });
        })
        .catch(() => {
          this.isLoggedIn = false;
          return undefined;
        });
    },

    /**
     * Checks if the current user is a Pristy admin.
     * The result is cached in the `isPristyAdmin` state.
     */
    async checkPristyAdminStatus() {
      // Only fetch if the status is unknown and user is logged in
      if (this.isPristyAdmin !== null || !this.person?.id) {
        return;
      }

      try {
        const data = await groupsApi.listGroupMembershipsForPerson(
          this.person.id,
        );
        this.isPristyAdmin = data.list.entries.some(
          (entry) => entry.entry.id === "GROUP_PRISTY_ADMINISTRATORS",
        );
      } catch (error) {
        console.error("Error checking Pristy admin status:", error);
        this.isPristyAdmin = false;
      }
    },

    /**
     * Attempt to login a user
     * @param {string} user Username
     * @param {string} password User password
     * @return {Promise<boolean | void>} Returns false if credentials are missing, void otherwise
     */
    async login(user, password) {
      if (!user || !password) {
        return false;
      }

      await this.apiLogin(user, password);
    },
    /**
     * Do a Basic Authent to Alfresco
     * @param {string} a Username
     * @param {string} p Password
     * @return {Promise<any>} Promise resolving to person data
     */
    apiLogin(a, p) {
      return alfrescoApi
        .login(a, p)
        .then(() => {
          this.isLoggedIn = true;
          return this.getPerson();
        })
        .catch((error) => {
          this.isLoggedIn = false;
          throw loginError(error);
        });
    },
    /**
     * Log Off Public Account ("user_publication_*")
     * @return {Promise<void>}
     */
    async publiclogoff() {
      const userProfile = localStorage.getItem("ACS_USERNAME");
      if (userProfile && userProfile.startsWith("user_publication_")) {
        alfrescoApi.invalidateSession();
        localStorage.removeItem("ticket-ECM");
        localStorage.removeItem("ACS_USERNAME");
      }
    },
    /**
     * Log In with the Public Account from a Public workspace.
     * @param {string} espace_id Workspace ID that have a Public Account
     * @return {Promise<string>} alf_ticket
     */
    publiclogin(espace_id) {
      let ticketECM = alfrescoApi.getTicketEcm();

      if (typeof ticketECM !== "undefined" && ticketECM != null) {
        // TODO check ticket validity
        return Promise.resolve(ticketECM);
      }
      return webscriptApi
        .executeWebScript(
          "GET",
          `fr/jeci/pristy/authentication/ticket/publication/site/${espace_id}`,
        )
        .then((alfticket) => {
          alfrescoApi.setTicket(alfticket.ticket, null);
          window.localStorage.setItem("ACS_USERNAME", alfticket.login);
          return alfticket.ticket;
        })
        .catch((error) => {
          throw loginError(error);
        });
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot));
}
